import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { ConfigGeneral, ConfigGeneralCreate, ConfigGeneralUpdate } from '@/modules/multichannel/types/configuracion'

interface ConfigGeneralFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: ConfigGeneral | null
  entityLabel: string
  onSubmit: (data: ConfigGeneralCreate) => void
  onUpdate: (id: number, data: ConfigGeneralUpdate) => void
  isSubmitting: boolean
}

interface FormData {
  nombre: string
  descripcion: string
}

export function ConfigGeneralFormDialog({
  open, onOpenChange, item, entityLabel, onSubmit, onUpdate, isSubmitting,
}: ConfigGeneralFormDialogProps) {
  const isEdit = !!item

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: { nombre: '', descripcion: '' },
  })

  useEffect(() => {
    if (open && item) {
      reset({ nombre: item.nombre, descripcion: item.descripcion || '' })
    } else if (!open) {
      reset({ nombre: '', descripcion: '' })
    }
  }, [open, item, reset])

  const onFormSubmit = (data: FormData) => {
    if (isEdit && item) {
      onUpdate(item.id, { nombre: data.nombre, descripcion: data.descripcion || undefined })
    } else {
      onSubmit({ nombre: data.nombre, descripcion: data.descripcion || undefined })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Editar ${entityLabel}` : `Nuevo ${entityLabel}`}</DialogTitle>
          <DialogDescription>
            {isEdit ? `Modifica el ${entityLabel.toLowerCase()}.` : `Crea un nuevo ${entityLabel.toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre <span className="text-destructive">*</span></Label>
            <Input id="nombre" {...register('nombre', { required: 'El nombre es requerido' })} disabled={isSubmitting} autoFocus />
            {errors.nombre && <p className="text-sm text-destructive">{errors.nombre.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripcion</Label>
            <Input id="descripcion" {...register('descripcion')} disabled={isSubmitting} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
